import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { Asset, AssetContract, Contract } from '../api/types'
import { SelettoreAnagrafica } from '../components/SelettoreAnagrafica'
import { SelettoreFornitore } from '../components/Fornitori'
import { Card, ErrorBanner, formatDateTime, PageSection, SectionedPage, Spinner } from '../components/common'
import { SchedaCampiPersonalizzati } from '../components/customFields'
import { DataTable, type Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { EditorTag } from '../components/Tag'

/** Scheda di un bene: fatti fisici, fatti amministrativi, copertura. */
export default function AssetDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const asset = useAsync(() => api.asset(id), [id])
  const catalog = useAsync(() => api.assetCatalog(), [])
  const contracts = useAsync(() => api.contracts(), [])
  const copertura = useAsync(() => api.assetContracts(id), [id])

  if (asset.loading && !asset.data) return <Spinner />
  if (asset.error && !asset.data) return <ErrorBanner error={asset.error} />
  if (!asset.data) return null

  const a = asset.data

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">
            {a.name ?? a.asset_tag} <span className="mono text-muted">{a.asset_tag}</span>
          </h1>
          <p className="page__subtitle">
            {[a.vendor, a.model].filter(Boolean).join(' ') || a.category}
            {a.ci_id && (
              <>
                {' · '}
                <Link to={`/cis/${a.ci_id}`}>
                  {a.ci_name ?? t('configuration item collegato')} →
                </Link>
              </>
            )}
          </p>
          <EditorTag tipo="asset" id={a.id} />
        </div>
        <Link to="/assets">{t('← Torna ai beni')}</Link>
      </header>

      {/* Un bene senza CI collegato non è un errore: può essere un ricambio in
          magazzino o una licenza. Ma se dovrebbe esserci, dirlo qui è l'unico modo
          di accorgersene. */}
      {!a.ci_id && (
        <div className="banner banner--info">
          {t('Questo bene non è collegato ad alcun configuration item: la rete non lo conferma. È normale per un ricambio, un bene in magazzino o una licenza; per un apparato in servizio significa che la discovery non lo vede.')}
        </div>
      )}

      {!a.covered && (
        <div className="banner banner--warn">
          {t('Nessun contratto valido copre questo bene oggi. Se dovrebbe essere coperto, verifica dal registro dei contratti: un contratto cessato, o una copertura con data di fine passata, non conta.')}{' '}
          <Link to="/contracts">{t('Apri il registro dei contratti →')}</Link>
        </div>
      )}

      <SectionedPage
        sections={[
          { id: 'scheda', label: t('Scheda del bene') },
          { id: 'copertura', label: t('Copertura contrattuale') },
          { id: 'tracciabilita', label: t('Tracciabilità') },
        ]}
      >
        <PageSection id="scheda">
          <AssetForm asset={a} statuses={catalog.data?.statuses ?? []} categories={catalog.data?.categories ?? []} onSaved={() => asset.reload()} onDeleted={() => navigate('/assets')} />
          {/* I campi che il tenant ha definito per questa categoria di bene.
              Compare solo se ce n'è almeno uno applicabile. */}
          <SchedaCampiPersonalizzati
            entity="asset"
            scope={a.category}
            values={a.custom_fields ?? {}}
            onSalva={async (valori) => {
              await api.updateAsset(a.id, { custom_fields: valori })
              asset.reload()
            }}
          />
        </PageSection>

        <PageSection id="copertura">
          <CoperturaContrattuale
            assetId={a.id}
            copertura={copertura.data ?? []}
            contratti={contracts.data ?? []}
            onChanged={() => {
              void copertura.reload()
              void asset.reload()
            }}
          />
        </PageSection>

        <PageSection id="tracciabilita">
          <Card title={t('Tracciabilità')}>
            <dl className="kv">
              <dt>{t('Registrato il')}</dt>
              <dd>{formatDateTime(a.created_at)}</dd>
              <dt>{t('Cartellino')}</dt>
              <dd className="mono">{a.asset_tag}</dd>
              <dt>{t('Serial Number')}</dt>
              <dd className="mono">{a.serial ?? '—'}</dd>
            </dl>
          </Card>
        </PageSection>
      </SectionedPage>
    </>
  )
}

/**
 * La copertura contrattuale del bene, nelle due direzioni.
 *
 * La copertura è molti-a-molti — un contratto quadro copre trecento apparati,
 * e lo stesso apparato può stare sotto manutenzione hardware e supporto
 * software di due fornitori — quindi la scheda mostra l'ELENCO, non un
 * booleano. Tutti i contratti, anche quelli non più validi: «quel guasto di
 * marzo era in garanzia?» ha una risposta solo se la copertura passata resta
 * visibile. La validità di oggi la dichiara il server, con gli stessi criteri
 * del conteggio: due definizioni di «coperto» divergerebbero alla prima
 * modifica.
 */
function CoperturaContrattuale({
  assetId,
  copertura,
  contratti,
  onChanged,
}: {
  assetId: string
  copertura: AssetContract[]
  contratti: Contract[]
  onChanged: () => void
}) {
  const [scelto, setScelto] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const giaCollegati = new Set(copertura.map((c) => c.contract_id))
  const agganciabili = contratti.filter((c) => !giaCollegati.has(c.id))

  async function aggancia() {
    if (!scelto) return
    setBusy(true)
    setError(null)
    try {
      await api.coverAssets(scelto, [assetId])
      setScelto('')
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function sgancia(contractId: string) {
    if (!window.confirm(t('Togliere il cespite dalla copertura di questo contratto? Non si può annullare.'))) return
    setBusy(true)
    setError(null)
    try {
      await api.uncoverAsset(contractId, assetId)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const periodo = (c: AssetContract) => (c.covered_from ?? '…') + ' → ' + (c.covered_until ?? c.end_date ?? '…')
  const colonne: Column<AssetContract>[] = [
    {
      key: 'reference',
      header: t('Contratto'),
      value: (c) => [c.reference, c.name].filter(Boolean).join(' — '),
      render: (c) => (
        <>
          <Link to={`/contracts/${c.contract_id}`}>{c.reference}</Link>
          {c.name && <span className="text-muted"> — {c.name}</span>}
        </>
      ),
    },
    { key: 'vendor', header: t('Fornitore') },
    { key: 'contract_type', header: t('Tipo'), value: (c) => t(c.contract_type) },
    { key: 'copertura', header: t('Copertura'), mono: true, value: periodo },
    {
      key: 'valid_today',
      header: t('Valido oggi'),
      value: (c) => (c.valid_today ? t('valido') : t('non valido')),
      render: (c) =>
        c.valid_today ? <span className="badge badge--ok">{t('valido')}</span> : <span className="badge badge--warn">{t('non valido')}</span>,
    },
    {
      key: 'azioni',
      header: '',
      sort: false,
      searchable: false,
      render: (c) => (
        <div style={{ textAlign: 'right' }}>
          {/* Sganciare cancella il collegamento e la sua storia: per un
              bene uscito a una data, meglio la data di fine copertura
              dal registro dei contratti. Il pulsante resta per gli
              agganci sbagliati. */}
          <button className="secondary" onClick={() => sgancia(c.contract_id)} disabled={busy}>
            {t('Sgancia')}
          </button>
        </div>
      ),
    },
  ]

  return (
    <Card title={t('Copertura contrattuale')}>
      <ErrorBanner error={error} />
      {copertura.length === 0 ? (
        <p className="text-muted">{t('Nessun contratto collegato a questo bene.')}</p>
      ) : (
        <DataTable
          rows={copertura}
          columns={colonne}
          rowKey={(c) => c.contract_id}
          conteggio={{ nome: t('contratti') }}
        />
      )}
      {agganciabili.length > 0 && (
        <div className="rowactions" style={{ textAlign: 'left', marginTop: 12 }}>
          <select value={scelto} onChange={(e) => setScelto(e.target.value)} disabled={busy}>
            <option value="">{t('Aggancia a un contratto…')}</option>
            {agganciabili.map((c) => (
              <option key={c.id} value={c.id}>
                {c.reference} · {c.vendor}
              </option>
            ))}
          </select>
          <button onClick={aggancia} disabled={busy || !scelto}>
            {busy ? t('Aggancio…') : t('Aggancia')}
          </button>
        </div>
      )}
    </Card>
  )
}


function AssetForm({
  asset,
  statuses,
  categories,
  onSaved,
  onDeleted,
}: {
  asset: Asset
  statuses: { value: string; label: string; hint: string }[]
  categories: { value: string; label: string }[]
  onSaved: () => void
  onDeleted: () => void
}) {
  const [form, setForm] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const anagrafiche = useAsync(() => api.masterDataTutte(), []).data

  useEffect(() => {
    setForm({
      name: asset.name ?? '',
      category: asset.category,
      status: asset.status,
      serial: asset.serial ?? '',
      vendor: asset.vendor ?? '',
      model: asset.model ?? '',
      location: asset.location ?? '',
      owner: asset.owner ?? '',
      cost_center: asset.cost_center ?? '',
      cost_center_id: asset.cost_center_id ?? '',
      org_unit_id: asset.org_unit_id ?? '',
      location_id: asset.location_id ?? '',
      supplier: asset.supplier ?? '',
      supplier_id: asset.supplier_id ?? '',
      purchase_order: asset.purchase_order ?? '',
      purchase_date: asset.purchase_date ?? '',
      purchase_price: asset.purchase_price !== null ? String(asset.purchase_price) : '',
      warranty_until: asset.warranty_until ?? '',
      end_of_life: asset.end_of_life ?? '',
      notes: asset.notes ?? '',
    })
  }, [asset])

  const set = (key: string) => (e: { target: { value: string } }) =>
    setForm({ ...form, [key]: e.target.value })

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.updateAsset(asset.id, {
        name: form.name || null,
        category: form.category,
        status: form.status,
        serial: form.serial || null,
        vendor: form.vendor || null,
        model: form.model || null,
        location: form.location || null,
        owner: form.owner || null,
        cost_center: form.cost_center || null,
        cost_center_id: form.cost_center_id || null,
        org_unit_id: form.org_unit_id || null,
        location_id: form.location_id || null,
        supplier: form.supplier || null,
        // Empty = recognized from the supplier as written (supplier_service), or none.
        supplier_id: form.supplier_id || null,
        purchase_order: form.purchase_order || null,
        purchase_date: form.purchase_date || null,
        // Stringa vuota → null e non 0: un prezzo non indicato non è un prezzo di
        // zero, e sommarlo come tale falserebbe il valore del parco.
        purchase_price: form.purchase_price ? Number(form.purchase_price) : null,
        warranty_until: form.warranty_until || null,
        end_of_life: form.end_of_life || null,
        notes: form.notes || null,
      })
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (
      !window.confirm(
        t('Eliminare la registrazione di {tag}?', { tag: asset.asset_tag }) +
          '\n\n' +
          t('Da usare solo per una registrazione sbagliata. Un bene venduto o rottamato va messo in stato «alienato»: la storia di cosa si è posseduto e per quanto serve all’inventario e a chi lo verifica.'),
      )
    )
      return
    setBusy(true)
    try {
      await api.deleteAsset(asset.id)
      onDeleted()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  const current = statuses.find((s) => s.value === form.status)

  return (
    <Card title={t('Scheda del bene')}>
      <ErrorBanner error={error} />

      <div className="row">
        <div className="field">
          <label htmlFor="f-name">{t('Nome')}</label>
          <input id="f-name" value={form.name ?? ''} onChange={set('name')} />
        </div>
        <div className="field" style={{ maxWidth: 220 }}>
          <label htmlFor="f-status">{t('Stato')}</label>
          <select id="f-status" value={form.status ?? ''} onChange={set('status')}>
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {t(s.label)}
              </option>
            ))}
          </select>
          {/* Lo stato è amministrativo, non tecnico: un server può essere «in
              servizio» e nel frattempo spento per manutenzione. Il suggerimento lo
              dice, perché la confusione fra i due è la più facile. */}
          <p className="field__hint">{current?.hint ? t(current.hint) : null}</p>
        </div>
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="f-category">{t('Categoria')}</label>
          <select id="f-category" value={form.category ?? ''} onChange={set('category')}>
            {categories.map((c) => (
              <option key={c.value} value={c.value}>
                {t(c.label)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="f-serial">{t('Serial Number')}</label>
          <input id="f-serial" value={form.serial ?? ''} onChange={set('serial')} />
          <p className="field__hint">
            {t('È quella che il fornitore usa per decidere se un apparato è in garanzia: meglio vuota che sbagliata.')}
          </p>
        </div>
        <div className="field">
          <label htmlFor="f-vendor">{t('Costruttore')}</label>
          <input id="f-vendor" value={form.vendor ?? ''} onChange={set('vendor')} />
        </div>
        <div className="field">
          <label htmlFor="f-model">{t('Modello')}</label>
          <input id="f-model" value={form.model ?? ''} onChange={set('model')} />
        </div>
        <div className="field">
          <label htmlFor="f-location-id">{t('Sede')}</label>
          <SelettoreAnagrafica id="f-location-id" kind="location" voci={anagrafiche?.location ?? []} value={form.location_id}
                               onChange={(v) => setForm({ ...form, location_id: v ?? '' })} />
          {form.location && <span className="muted eagle-piccolo">{t('testo libero precedente: {v}', { v: form.location })}</span>}
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label htmlFor="f-owner">{t('Proprietario')}</label>
          <input id="f-owner" value={form.owner ?? ''} onChange={set('owner')} />
        </div>
        <div className="field">
          <label htmlFor="f-ou">{t('Unità organizzativa')}</label>
          <SelettoreAnagrafica id="f-ou" kind="org_unit" voci={anagrafiche?.org_unit ?? []} value={form.org_unit_id}
                               onChange={(v) => setForm({ ...form, org_unit_id: v ?? '' })} />
        </div>
        <div className="field">
          <label htmlFor="f-cc">{t('Centro di costo')}</label>
          <SelettoreAnagrafica id="f-cc" kind="cost_center" voci={anagrafiche?.cost_center ?? []} value={form.cost_center_id}
                               onChange={(v) => setForm({ ...form, cost_center_id: v ?? '' })} />
          {form.cost_center && <span className="muted eagle-piccolo">{t('testo libero precedente: {v}', { v: form.cost_center })}</span>}
        </div>
        <div className="field">
          <label htmlFor="f-supplier">{t('Fornitore')}</label>
          <input id="f-supplier" value={form.supplier ?? ''} onChange={set('supplier')} />
          <SelettoreFornitore value={form.supplier_id || null} onChange={(id) => setForm({ ...form, supplier_id: id ?? '' })} />
        </div>
        <div className="field">
          <label htmlFor="f-po">{t('Ordine')}</label>
          <input id="f-po" value={form.purchase_order ?? ''} onChange={set('purchase_order')} />
        </div>
      </div>

      <div className="row">
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="f-pdate">{t('Data di acquisto')}</label>
          <input
            id="f-pdate"
            type="date"
            value={form.purchase_date ?? ''}
            onChange={set('purchase_date')}
          />
        </div>
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="f-price">{t('Prezzo ({valuta})', { valuta: asset.currency })}</label>
          <input
            id="f-price"
            type="number"
            step="0.01"
            min="0"
            value={form.purchase_price ?? ''}
            onChange={set('purchase_price')}
          />
        </div>
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="f-warranty">{t('Garanzia fino al')}</label>
          <input
            id="f-warranty"
            type="date"
            value={form.warranty_until ?? ''}
            onChange={set('warranty_until')}
          />
        </div>
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="f-eol">{t('Fine vita prevista')}</label>
          <input id="f-eol" type="date" value={form.end_of_life ?? ''} onChange={set('end_of_life')} />
          <p className="field__hint">
            {t('Diversa dalla garanzia: quella dice fino a quando qualcuno lo ripara, questa fino a quando lo si vuole tenere.')}
          </p>
        </div>
      </div>

      <div className="field">
        <label htmlFor="f-notes">{t('Note')}</label>
        <input id="f-notes" value={form.notes ?? ''} onChange={set('notes')} />
      </div>

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={save} disabled={busy}>
          {busy ? t('Salvataggio…') : t('Salva')}
        </button>
        <button className="danger" onClick={remove} disabled={busy}>
          {t('Elimina registrazione')}
        </button>
      </div>
    </Card>
  )
}
